import { useState, Fragment } from 'react';
import moment from 'moment';
import { makeStyles } from 'tss-react/mui';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import Button from '@mui/material/Button';
import { momentParse } from '../utils/time';
import config from '../config';
import MaterialLink from './material-link';
import ConfirmButton from './confirm-button';
import CopyInput from './copy-input';
import type { GroupInvite, EventInvite } from '../types/Invite';

const useStyles = makeStyles()((theme) => ({
  buttons: {
    marginTop: theme.spacing(1),
    '& > *:not(:last-child)': {
      marginRight: theme.spacing(1)
    }
  },
  meta: {
    margin: theme.spacing(1, 0),
    color: theme.palette.text.secondary,
  },
  list: {
    // paddingLeft: 0,
    // marginLeft: 0,
    // listStyleType: 'none',
    '& > li:not(:last-child)': {
      marginBottom: theme.spacing(2)
    }
  },
  email: {
    display: 'flex',
    alignItems: 'center',
    color: '#000',
    lineHeight: '16px'
  },
  emailIcon: {
    marginRight: theme.spacing(0.5),
    fontSize: 16
  },
  state: {
    fontWeight: 'bold'
  },
}));

type Props = {
  invites?: (GroupInvite | EventInvite)[];
  basePath?: string;
  expireInvite?: (...args: unknown[]) => Promise<unknown>;
  extendInvite?: (...args: unknown[]) => Promise<unknown>;
  resendEmailInvite?: (...args: unknown[]) => Promise<unknown>;
};

const InviteList = (props: Props) => {
  const { invites, basePath, expireInvite, extendInvite, resendEmailInvite } = props;
  const { classes } = useStyles();
  const [submitting, setSubmitting] = useState(false);

  if (!invites || !invites.length) {
    return <p>No invites</p>;
  }

  return (
    <ul className={classes.list}>
      {invites.map((invite) => {
        const createdAt = momentParse(invite.createdAt);
        const expiresAt = momentParse(invite.expiresAt);
        const expired = expiresAt.diff(moment()) <= 0;
        const inviteUrl = `${config().universal.baseUri}${basePath}?join=${invite.id}`;
        const member = ('groupMember' in invite ? (invite as GroupInvite).groupMember : (invite as EventInvite).eventMember) || null;
        return (
          <li key={invite.id}>
            {invite.inviteType === 'EMAIL' ? (
              <span className={classes.email}>
                <MailOutlineIcon className={classes.emailIcon} />
                {invite.email}
              </span>
            ) : (
              <CopyInput text={inviteUrl} disabled={expired} />
            )}
            <div className={classes.meta}>
              <div>
                {invite.inviteType === 'EMAIL' ? (
                  <Fragment>
                    {member ? (
                      <Fragment>
                        Accepted by
                        {' '}
                        <MaterialLink
                          href={`/people/${member.user.username}`}
                        >
                          {member.user.displayName}
                        </MaterialLink>
                      </Fragment>
                    ) : (
                      <Fragment>
                        {expired ? (
                          <span className={classes.state}>Expired</span>
                        ) : (
                          <Fragment>
                            <span className={classes.state}>Pending</span>
                            {' – '}
                            Expires {expiresAt.format('MMMM Do, YYYY h:mm a')}
                          </Fragment>
                        )}
                      </Fragment>
                    )}
                  </Fragment>
                ) : (
                  <Fragment>
                    {expired ? (
                      <span className={classes.state}>Expired</span>
                    ) : (
                      <Fragment>
                        <span className={classes.state}>Active</span>
                        {' – '}
                        Expires {expiresAt.format('MMMM Do, YYYY h:mm a')}
                      </Fragment>
                    )}
                    {' – '}
                    {invite.memberCount} member{invite.memberCount !== 1 ? 's' : ''}
                  </Fragment>
                )}
                {` – Created ${createdAt.format('MMMM Do, YYYY')} by `}
                <MaterialLink
                  href={`/people/${invite.createdByUser.username}`}
                >
                  {invite.createdByUser.displayName}
                </MaterialLink>
              </div>
            </div>
            {(!expired || (invite.inviteType === 'EMAIL' && !('groupMember' in invite && invite.groupMember))) && (
              <div className={classes.buttons}>
                {!expired && invite.inviteType === 'EMAIL' && !('groupMember' in invite && invite.groupMember) && (
                  <ConfirmButton
                    onClick={(e) => {
                      (e as MouseEvent).preventDefault();
                      setSubmitting(true);
                      resendEmailInvite({
                        variables: { id: invite.id }
                      })
                        .then(() => {
                          alert('Invite re-sent!');
                          setSubmitting(false);
                        })
                        .catch(() => {
                          alert('An error occurred.')
                          setSubmitting(false);
                        });
                    }}
                    variant="outlined"
                    size="small"
                    disabled={submitting}
                    label="Re-send Invite"
                    confirmMessage={`Are you sure you wish to re-send this invite to ${invite.email}?`}
                  />
                )}
                <Button
                  onClick={(e) => {
                    e.preventDefault();
                    setSubmitting(true);
                    extendInvite({
                      variables: { id: invite.id }
                    })
                      .then(() => setSubmitting(false))
                      .catch(() => {
                        alert('An error occurred.')
                        setSubmitting(false);
                      });
                  }}
                  variant="outlined"
                  size="small"
                  disabled={submitting}
                >
                  Extend Expiration
                </Button>
                <ConfirmButton
                  onClick={(e) => {
                    (e as MouseEvent).preventDefault();
                    setSubmitting(true);
                    expireInvite({
                      variables: { id: invite.id }
                    })
                      .then(() => setSubmitting(false))
                      .catch(() => {
                        alert('An error occurred.')
                        setSubmitting(false);
                      });
                  }}
                  label="Expire"
                  confirmMessage="Are you sure you wish to expire this invite?"
                  disabled={submitting}
                  size="small"
                />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default InviteList;
